import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TopNav } from "../components/TopNav";
import { DestinationCard } from "../components/DestinationCard";
import { listSaved } from "../lib/api";
import { localSavedStore } from "../lib/savedStore";
import { useAuth } from "../lib/AuthContext";
import { Bookmark, Sparkles, Trash2 } from "lucide-react";
import { PromptBar } from "../components/PromptBar";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);

  const load = async () => {
    setFetching(true);
    const local = localSavedStore.list();
    let server = [];
    if (user) {
      try { server = await listSaved(); } catch (_) {}
    }
    // merge by id, prefer server
    const map = new Map();
    for (const t of local) map.set(t.id, t);
    for (const t of server) map.set(t.id, t);
    const merged = Array.from(map.values()).sort((a, b) => (b.saved_at || 0) - (a.saved_at || 0));
    setItems(merged);
    setFetching(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const removeOne = (id) => {
    localSavedStore.remove(id);
    setItems((xs) => xs.filter((x) => x.id !== id));
    toast.success("Removed");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />
      <main className="pt-28 px-5 sm:px-8 pb-24">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FE0979] flex items-center gap-2 mb-2">
                <Bookmark className="h-3.5 w-3.5" /> Your trips
              </div>
              <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight">
                {user ? `Welcome back, ${user.name?.split(" ")[0] || "creator"}.` : "Saved trips."}
              </h1>
              <p className="text-zinc-400 mt-2 text-sm sm:text-base max-w-xl">
                {user ? "Saved itineraries, AI-crafted vibes, all in one place." : "These are saved on this device. Sign in to sync across devices."}
              </p>
            </div>
            {!user && (
              <Link
                to="/login"
                data-testid="dashboard-signin"
                className="glass rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-zinc-200 hover:text-white hover:bg-white/10 transition"
              >
                Sign in to sync
              </Link>
            )}
          </div>

          <div className="mb-10">
            <PromptBar />
          </div>

          {fetching ? (
            <div className="text-zinc-500 py-16 text-center">Loading your saves…</div>
          ) : items.length === 0 ? (
            <div className="glass rounded-3xl p-8 sm:p-12 text-center" data-testid="dashboard-empty">
              <Sparkles className="h-6 w-6 text-[#00F2FE] mx-auto" />
              <h2 className="font-display font-bold text-2xl mt-3">No saved trips yet</h2>
              <p className="text-zinc-400 mt-2 max-w-md mx-auto text-sm">Generate an itinerary above and tap Save — it'll show up here. No login needed.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="saved-grid">
              {items.map((it, i) => (
                <div key={it.id} className="relative group" data-testid={`saved-card-${it.id}`}>
                  <DestinationCard item={it} index={i} />
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeOne(it.id); }}
                    data-testid={`saved-remove-${it.id}`}
                    title="Remove"
                    className="absolute top-3 right-12 z-10 glass rounded-full p-2 opacity-0 group-hover:opacity-100 hover:bg-white/15 transition"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-zinc-300" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
